import { useState } from 'react'
import { X } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { useUpdateProfile } from '../api/hooks/useProfile'

// #gender-required-alerts (Part 1C) — one-time-per-session prompt for
// existing users whose profile has gender = NULL. New accounts can't
// reach this state (Part 1A gates signup on gender), so this modal
// eventually drains its cohort as the ~10 legacy accounts pick.
//
// Non-blocking by design: users can dismiss with "Later" and keep
// using the app. Dismissal writes a sessionStorage flag so the modal
// doesn't re-fire on every page navigation within the same session;
// the flag lives in sessionStorage (not localStorage) so the modal
// resurfaces on the next fresh session per dispatch requirement.
//
// Mounted once from AppShell — never conditionally rendered by caller
// beyond the null-user short-circuit. Internal state decides whether
// to show the overlay.

const DISMISS_KEY = 'genderPromptDismissed'

export function GenderPromptModal() {
  const user = useAuthStore((s) => s.user)
  const setAuth = useAuthStore((s) => s.setAuth)
  const token = useAuthStore((s) => s.token)
  const updateProfile = useUpdateProfile()

  // Read dismissal once at mount — sessionStorage is synchronous +
  // cheap. Re-reading on every render would fight React's render
  // predictability.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1' } catch { return false }
  })
  const [saving, setSaving] = useState(false)

  // Short-circuit: don't render until we know the user is signed in
  // AND their gender is genuinely missing. undefined / '' should not
  // trigger the prompt — those are stale-load states, not the null
  // that Part 1A / Part 1C target.
  if (!user) return null
  const needsPrompt = user.gender === null
  if (!needsPrompt) return null
  if (dismissed) return null

  const handleDismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1') } catch { /* ignore quota */ }
    setDismissed(true)
  }

  const handlePick = async (g: 'male' | 'female') => {
    if (saving) return
    setSaving(true)
    try {
      await updateProfile.mutateAsync({ userId: user.id, form: { gender: g } })
      // Reflect into auth store so downstream consumers (RelationPicker,
      // ConfirmDialog) see the new gender without a page reload.
      if (token) setAuth({ ...user, gender: g }, token)
      // No need to persist to sessionStorage — the needsPrompt check
      // now returns false.
    } catch {
      // Silent — user can retry via ProfileEdit if the write fails.
      // Keep the modal open so they can try again or dismiss.
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="text-navy font-bold text-base">Gender required</div>
          <button onClick={handleDismiss} className="p-1" aria-label="Dismiss">
            <X size={20} className="text-navy" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-navy text-sm">
            దయచేసి మీ gender (Male/Female) ఎంపిక చేయండి — ఇది మీ బంధుత్వ సంబంధాలను సరిగ్గా చూపించడానికి అవసరం.
          </p>
          <p className="text-gray-500 text-xs">
            Please select your gender (Male/Female) — it&apos;s needed to display your kinship relations correctly.
          </p>
          <div className="flex gap-2 pt-2">
            {(['male', 'female'] as const).map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => handlePick(g)}
                disabled={saving}
                className="flex-1 py-2.5 rounded-lg border-2 border-navy text-navy font-semibold text-sm hover:bg-navy hover:text-white transition disabled:opacity-60"
              >
                {g === 'male' ? 'Male' : 'Female'}
              </button>
            ))}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border">
          <button
            type="button"
            onClick={handleDismiss}
            disabled={saving}
            className="w-full text-navy/70 text-xs font-semibold hover:underline disabled:opacity-60"
          >
            Later
          </button>
        </div>
      </div>
    </div>
  )
}
